import React, { useEffect, useState } from 'react'
import { useParams } from 'react-router'

// Third party
import { where } from 'firebase/firestore'
import { useIonRouter } from '@ionic/react'

// Constants
import { CONST } from '../../../const/const'

// Components
import ExhibitorProfileHeadSection from './ExhibitorProfileHeadSection'
import ExhibitorProfilePageWrapper from './ExhibitorProfilePageWrapper'
import ExhibitorProfileDisplayTab from './ExhibitorProfileDisplayTab'

// Models
import { TEventRegisteredUsers } from '../../../models/event-registered-users/event-registered-users.interface'
import { EventRegisteredUsersModel } from '../../../models/event-registered-users/event-registered-users.model'
import { UserModel } from '../../../models/users/user.model'

// Redux
import {
  setScratchEventId,
  setScratchRegistrationId,
  setScratchViewVisibility,
} from '../../../store/events/eventsSlice'
import {
  getRecipientThunk,
  resetRecipientAc,
  selectExhibitorDetails,
  selectRecipientR,
  selectRegistrationR,
  setExhibitorDetails,
  setRegistration,
} from '../../../store/exhibitor/exhibitorSlice'
import { useAppDispatch, useAppSelector } from '../../../store/hooks'

// Helpers
import { getConvertedData } from '../../../models/interface.helper'

// Hooks
import useToasterHelper from '../../../helpers/ToasterHelper'
import { CustomError } from '../../../helpers/helpers'
import FirestoreService from '../../../services/firestoreService'

import helpers from '../../../commonHelpers/helpers'
import { MESSAGES_CONST } from '../../../const/messages-const'

import { EventReviewPublishModel } from '../../../models/event-review-publish/event-review-publish.model'
import { IEventReviewPublish } from '../../../models/event-review-publish/event-review-publish.interface'

// %%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%

// Types
type Props = {
  handleModal: (showHide: boolean, typeOfModal: string, data?: Object) => void
}

type IUserParams = {
  registrarId: string
  eventId: string
  registrationId: string
}

// Constants

const COLLECTIONS = CONST.DATA.FIRESTORE.LATEST.COLLECTIONS

const FILE_NAME = 'ExhibitorProfileRevenuePage'

const customErrorProps = {
  fileName: FILE_NAME,
  message: MESSAGES_CONST.SOMETHING_WENT_WRONG,
}

// %%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%

const ExhibitorProfileRevenuePage = (props: Props) => {
  // Hooks and vars
  const Router = useIonRouter()
  const dispatch = useAppDispatch()
  const toastFunction = useToasterHelper()

  const { registrarId, registrationId, eventId } = useParams<IUserParams>()
  const isrecipient = Router.routeInfo.search ? Router.routeInfo.search.split('=')[1] : 'false'

  const [profileLoading, setProfileLoading] = useState(false)
  const [showScratchedView, setShowScratchedView] = useState(false)
  const [loading, setLoading] = useState(true)
  const [eventData, setEventData] = useState<IEventReviewPublish | null>(null)

  const activeTab = useState<string>(CONST.UI.EXHIBITOR.TABS.CLASSES.VALUE)[0]

  const recipientR = useAppSelector(selectRecipientR)
  const registrationR = useAppSelector(selectRegistrationR)
  const recipient = useAppSelector(selectExhibitorDetails)

  const uiLoading = profileLoading || recipientR.status === 'loading'

  useEffect(() => {
    return () => {
      dispatch(resetRecipientAc())
      dispatch(setExhibitorDetails(null))
    }
  }, [dispatch])

  const getRegistration = async () => {
    setLoading(true)

    let eventRegisteredUsers: TEventRegisteredUsers | null = null

    const registeredUsersSnaps = await FirestoreService.filterItems(
      COLLECTIONS.EVENT_REGISTERED_USERS.NAME,
      [
        where(COLLECTIONS.EVENT_REGISTERED_USERS.FIELDS.EVENT_ID.KEY, '==', eventId),
        where(COLLECTIONS.EVENT_REGISTERED_USERS.FIELDS.USER_ID.KEY, '==', registrarId),
      ]
    )

    registeredUsersSnaps.docs.forEach((currDoc) => {
      eventRegisteredUsers = getConvertedData(
        EventRegisteredUsersModel.fromFirestoreDoc(currDoc).toObject()
      )
    })

    if (eventRegisteredUsers) dispatch(setRegistration(eventRegisteredUsers))

    setLoading(false)
  }

  const handleGetPublishEvent = async (eventId: string) => {
    const publishedEventSnapShot = await FirestoreService.getItem(
      COLLECTIONS.EVENT_REVIEW_PUBLISH.NAME,
      eventId
    )
    let publishedEvent = EventReviewPublishModel.fromFirestoreDoc(publishedEventSnapShot).toObject()
    if (setEventData) setEventData(publishedEvent)

    return publishedEvent
  }

  useEffect(() => {
    dispatch(setScratchEventId(eventId))
    dispatch(setScratchRegistrationId(registrationId))
    getRegistration().then()
    handleGetPublishEvent(eventId).then()
  }, [eventId, registrationId])

  useEffect(() => {
    if (registrarId) {
      setProfileLoading(true)
      getUserData().then()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [registrarId])

  useEffect(() => {
    const registratioIdNRecipietIdPresent = registrationId && registrarId
    const recipientIsDifferent = recipientR.data?.recipientId !== registrarId
    const allowDispatch = registratioIdNRecipietIdPresent && recipientIsDifferent

    if (allowDispatch) {
      dispatch(
        getRecipientThunk({
          recipientId: registrarId,
          registrationId,
        })
      )
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [registrationId, registrarId, recipient])

  // Functions
  const handleScratchView = (visible: boolean) => {
    dispatch(setScratchViewVisibility(visible))
    setShowScratchedView(visible)
  }

  async function getUserData() {
    try {
      const userSnapshot = await FirestoreService.getItem(COLLECTIONS.USERS.NAME, registrarId)

      if (!userSnapshot.exists())
        throw CustomError.somethingWentWrong({
          ...customErrorProps,
          moduleName: 'getUserData',
          devMessage: `recipient with id: ${registrarId} does not exist`,
        })
      else {
        let user = UserModel.fromFirestoreDoc(userSnapshot).toObject()

        dispatch(setExhibitorDetails(getConvertedData(user)))
      }
    } catch (err) {
      toastFunction.error({
        message: MESSAGES_CONST.SOMETHING_WENT_WRONG,
      })

      helpers.logger({
        isError: true,
        message: err,
      })
    } finally {
      setProfileLoading(false)
    }
  }

  if (recipientR.status === 'failed') {
    return (
      <ExhibitorProfilePageWrapper
        title="Back"
        link={`${CONST.ROUTES.MANAGE.REVENUE.SUB_URLS.REGISTRATION.URL}/${eventId}`}
      >
        <div className="p-4 text-sm text-red-600 rounded-lg bg-red-50 font-bold" role="alert">
          {recipientR.message}
        </div>
      </ExhibitorProfilePageWrapper>
    )
  }

  return (
    <>
      <ExhibitorProfilePageWrapper
        title="Back"
        link={`${CONST.ROUTES.MANAGE.REVENUE.SUB_URLS.REGISTRATION.URL}/${eventId}`}
      >
        <ExhibitorProfileHeadSection
          backUrl={`${CONST.ROUTES.MANAGE.REVENUE.SUB_URLS.REGISTRATION.URL}/${eventId}`}
          isRefund={true}
          loading={uiLoading}
          activeTab={activeTab}
          exhibitor={recipient}
          showScratchedView={false}
          recipient={recipientR.data}
          handleModal={props.handleModal}
          exhibitorAllData={registrationR}
          handleScratchView={handleScratchView}
        />
        {!uiLoading || loading ? (
          <>
            <div className="my-4 border-t border-solid border-[#D3DAEE]"></div>
            <ExhibitorProfileDisplayTab
              isRecipient={isrecipient === 'true'}
              registrationId={registrationId}
              eventId={eventId}
              eventData={eventData}
              exhibitorAllData={registrationR}
              showScratchedView={showScratchedView}
              handleScratchView={handleScratchView}
            />
          </>
        ) : null}
      </ExhibitorProfilePageWrapper>
    </>
  )
}

export default ExhibitorProfileRevenuePage
